import { useContext } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { useHapticPress } from '../../../assets/hooks/useHapticPress';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import './LegalDocumentPopup.css';

const POPUP_TRANSITION = {
  duration: 0.34,
  ease: [0.22, 1, 0.36, 1],
};

const CrossIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      fill="currentColor"
      d="M17.293 5.293a1 1 0 1 1 1.414 1.414L13.414 12l5.293 5.293a1 1 0 1 1-1.414 1.414L12 13.414l-5.293 5.293a1 1 0 1 1-1.414-1.414L10.586 12 5.293 6.707a1 1 0 1 1 1.414-1.414L12 10.586z"
    />
  </svg>
);

function getLocalizedValue(value, locale = 'en') {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  const localizedValue = value[locale] ?? value.en;

  if (typeof localizedValue === 'string') {
    return localizedValue;
  }

  const firstString = Object.values(value).find((item) => typeof item === 'string');

  return firstString || '';
}

function getDocumentTitle(document, locale, fallbackTitle) {
  return getLocalizedValue(document?.title, locale) || document?.slug || fallbackTitle;
}

function getDocumentEyebrow(document, locale, fallbackTitle) {
  return getLocalizedValue(document?.eyebrow, locale) || fallbackTitle;
}

function formatDocumentDate(value, locale, documentsTranslation, language) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return documentsTranslation.validFrom.replace('{{date}}', value);
  }

  const formattedDate = new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);

  return documentsTranslation.validFrom.replace('{{date}}', formattedDate);
}

function getDocumentMeta(document, locale, documentsTranslation, language) {
  return [
    Number.isInteger(document?.version)
      ? documentsTranslation.version.replace('{{version}}', document.version)
      : null,
    formatDocumentDate(document?.valid_from, locale, documentsTranslation, language),
  ].filter(Boolean);
}

function getBlockItemText(item, locale) {
  return (
    getLocalizedValue(item?.text, locale)
    || getLocalizedValue(item?.title, locale)
    || getLocalizedValue(item, locale)
  );
}

function LegalDocumentContent({ document, locale, documentsTranslation }) {
  const sections = Array.isArray(document?.sections) ? document.sections : [];
  const description = getLocalizedValue(document?.description, locale);

  if (sections.length === 0) {
    return (
      <div className="legal-document-popup__empty">
        {description || documentsTranslation.contentUnavailable}
      </div>
    );
  }

  return (
    <div className="legal-document-popup__document">
      {description && (
        <p className="legal-document-popup__lead">{description}</p>
      )}
      {sections.map((section, sectionIndex) => {
        const title = getLocalizedValue(section?.title, locale);
        const blocks = Array.isArray(section?.blocks) ? section.blocks : [];

        return (
          <section
            className="legal-document-popup__section"
            key={`${title || 'section'}-${sectionIndex}`}
          >
            {title && <h3 className="legal-document-popup__section-title">{title}</h3>}
            {blocks.map((block, blockIndex) => {
              if (block?.type === 'list') {
                const items = Array.isArray(block.items) ? block.items : [];

                return (
                  <ul
                    className="legal-document-popup__list"
                    key={`block-${blockIndex}`}
                  >
                    {items.map((item, itemIndex) => (
                      <li key={`item-${itemIndex}`}>{getBlockItemText(item, locale)}</li>
                    ))}
                  </ul>
                );
              }

              const text = getLocalizedValue(block?.text, locale);

              return text ? (
                <p className="legal-document-popup__paragraph" key={`block-${blockIndex}`}>
                  {text}
                </p>
              ) : null;
            })}
          </section>
        );
      })}
    </div>
  );
}

function LegalDocumentPopup({
  document,
  error,
  isLoading,
  locale,
  onClose,
  onRetry,
}) {
  const { webApp } = useTelegram();
  const { common, documents: documentsTranslation, language } = useContext(TranslationContext);
  const hapticProps = useHapticPress(webApp);
  const shouldReduceMotion = useReducedMotion();
  const documentLocale = locale || language || 'en';
  const meta = getDocumentMeta(document, documentLocale, documentsTranslation, language);
  const motionProps = shouldReduceMotion
    ? {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.12 },
    }
    : {
      initial: {
        opacity: 0,
        transform: 'translate3d(0, 28px, 0)',
        filter: 'blur(8px)',
      },
      animate: {
        opacity: 1,
        transform: 'translate3d(0, 0px, 0)',
        filter: 'blur(0px)',
      },
      exit: {
        opacity: 0,
        transform: 'translate3d(0, 18px, 0)',
        filter: 'blur(8px)',
      },
      transition: POPUP_TRANSITION,
    };

  if (!document) {
    return null;
  }

  return (
    <motion.div
      className="legal-document-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-document-popup-title"
      {...motionProps}
    >
      <header className="legal-document-popup__header">
        <div className="legal-document-popup__heading">
          <p className="legal-document-popup__eyebrow">
            {getDocumentEyebrow(document, documentLocale, documentsTranslation.legalDocument)}
          </p>
          <h2 id="legal-document-popup-title" className="legal-document-popup__title">
            {getDocumentTitle(document, documentLocale, documentsTranslation.legalDocument)}
          </h2>
          {meta.length > 0 && (
            <div className="legal-document-popup__meta">
              {meta.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="legal-document-popup__close"
          aria-label={common.closeDocument}
          onClick={onClose}
          {...hapticProps}
        >
          <CrossIcon className="legal-document-popup__close-icon" />
        </button>
      </header>

      <div className="legal-document-popup__body">
        {isLoading ? (
          <MiniPreloader
            className="legal-document-popup__loader"
            label={documentsTranslation.loadingDocument}
          />
        ) : error ? (
          <div className="legal-document-popup__error" role="alert">
            <p>{error}</p>
            <button
              type="button"
              className="legal-document-popup__retry"
              onClick={onRetry}
              {...hapticProps}
            >
              {common.tryAgain}
            </button>
          </div>
        ) : (
          <LegalDocumentContent
            document={document}
            documentsTranslation={documentsTranslation}
            locale={documentLocale}
          />
        )}
      </div>
    </motion.div>
  );
}

export default LegalDocumentPopup;
